import { Box, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { BRAZIL_UFS, type BrazilAddressFields } from '~/utils/brazilAddress';
import { StandardJourneyFieldError, StandardJourneyInput } from './index';
import type { JourneyAddressErrors } from './useJourneyAddressForm';

type Props = {
  appId: string;
  form: BrazilAddressFields;
  errors: JourneyAddressErrors;
  onChange: (field: keyof BrazilAddressFields, value: string) => void;
  onZipcodeChange: (value: string) => void;
  /** `compact` (card de 1/3 da revisão) põe Número/Bairro lado a lado; `wide` segue o Figma do cobrança. */
  layout?: 'compact' | 'wide';
};

/** Campos de endereço brasileiro (CEP, endereço, número, bairro, complemento, UF em lista e cidade). */
export default function JourneyAddressFields({
  appId,
  form,
  errors,
  onChange,
  onZipcodeChange,
  layout = 'compact'
}: Props) {
  const field = (name: keyof BrazilAddressFields, label: string, mask?: 'zipcode') => (
    <StandardJourneyInput
      appId={`${appId}-${name}`}
      error={Boolean(errors[name])}
      errorMessage={errors[name]}
      label={label}
      mask={mask}
      onChange={name === 'zipcode' ? onZipcodeChange : (value) => onChange(name, value)}
      value={form[name]}
    />
  );

  const stateLabelId = `${appId}-state-label`;
  const twoColumns = { display: 'grid', gap: '8px', gridTemplateColumns: '1fr 1fr' } as const;

  const stateSelect = (
    <FormControl error={Boolean(errors.state)} fullWidth size="small" sx={{ mt: 1 }}>
      <InputLabel id={stateLabelId} shrink sx={{ position: 'static', transform: 'none' }}>
        Estado/ UF
      </InputLabel>
      <Select
        data-app-id={`${appId}-state`}
        displayEmpty
        labelId={stateLabelId}
        notched={false}
        onChange={(event) => onChange('state', String(event.target.value))}
        value={(BRAZIL_UFS as readonly string[]).includes(form.state) ? form.state : ''}
      >
        <MenuItem disabled value="">
          UF
        </MenuItem>
        {BRAZIL_UFS.map((uf) => (
          <MenuItem key={uf} value={uf}>
            {uf}
          </MenuItem>
        ))}
      </Select>
      {errors.state ? (
        <StandardJourneyFieldError appId={`${appId}-state-error`} id={`${appId}-state-error`} message={errors.state} />
      ) : null}
    </FormControl>
  );

  if (layout === 'wide') {
    return (
      <Box data-app-id={appId} sx={{ display: 'flex', flexDirection: 'column' }}>
        {field('zipcode', 'CEP', 'zipcode')}
        <Box sx={{ display: 'grid', gap: '8px', gridTemplateColumns: { sm: '3fr 1fr', xs: '1fr' } }}>
          {field('street', 'Endereço')}
          {field('number', 'Número')}
        </Box>
        <Box sx={twoColumns}>
          {field('neighborhood', 'Bairro')}
          {field('additional_details', 'Complemento')}
        </Box>
        <Box sx={twoColumns}>
          {stateSelect}
          {field('city', 'Cidade')}
        </Box>
      </Box>
    );
  }

  return (
    <Box data-app-id={appId} sx={{ display: 'flex', flexDirection: 'column' }}>
      {field('zipcode', 'CEP', 'zipcode')}
      {field('street', 'Endereço')}
      <Box sx={twoColumns}>
        {field('number', 'Número')}
        {field('neighborhood', 'Bairro')}
      </Box>
      {field('additional_details', 'Complemento')}
      <Box sx={twoColumns}>
        {stateSelect}
        {field('city', 'Cidade')}
      </Box>
    </Box>
  );
}
